import {Component, computed, inject, Signal} from '@angular/core';
import {CommonModule} from '@angular/common';

import {DimensionItemNode, DimensionItemsFacade} from '@selpia/dimension-items/data-access';
import {LoaderComponent, MapEntryPipe, TreeDropdownComponent} from '@selpia/shared/ui';
import {TreeNodePipe} from '@selpia/dimension-items/pipes';
import {PerspectiveFacade} from '@selpia/perspective/data-access';
import {ArrayTools} from '@selpia/shared/utils';
import {NavigationService} from '@selpia/data-batch/history-access';
import {DimensionItem} from '@selpia/shared/models';
import {MonitoringService} from '@selpia/user/tracking';

import {DimensionFilterHelper} from '../utils/dimension-item-filter-helper';
import {DIMENSION_LEVEL_SERVICE} from '../dimension-level-service';

@Component({
  selector: 'lib-feature-tree-filters',
  imports: [CommonModule, LoaderComponent, TreeDropdownComponent, TreeNodePipe, MapEntryPipe],
  templateUrl: './feature-tree-filters.component.html',
  styleUrl: './feature-tree-filters.component.scss',
})
export class FeatureTreeFiltersComponent {
  readonly #dimensionItemsFacade = inject(DimensionItemsFacade);
  readonly #perspectiveFacade = inject(PerspectiveFacade);
  readonly #navigationService = inject(NavigationService);
  readonly #dimensionLevelService = inject(DIMENSION_LEVEL_SERVICE);
  readonly #monitoringService = inject(MonitoringService);

  protected readonly dropDownsAreLoading: Signal<boolean> = computed(
    () => this.#dimensionItemsFacade.isLoading() || !this.#perspectiveFacade.perspective(),
  );

  protected readonly dimensionItemsTreeMap: Signal<Map<string, DimensionItemNode> | undefined> =
    this.#dimensionItemsFacade.itemsTrees;

  protected readonly filterItemsByDimensionName: Signal<Map<string, DimensionItem | undefined>> = computed(() => {
    const currentFilters = this.#perspectiveFacade.perspective()?.filterDimensions;

    if (!currentFilters) {
      return new Map();
    }

    return DimensionFilterHelper.getAsItemsWithDimensionName(currentFilters);
  });

  onFilterSelected(dimensionItem: DimensionItem): void {
    const dimension = dimensionItem.dimension;

    const updatedFilters = new Map(this.filterItemsByDimensionName()).set(
      dimension.name,
      dimensionItem.parentCode ? dimensionItem : undefined,
    );

    const perspective = this.#perspectiveFacade.perspective();

    this.#navigationService.updatePerspective({
      focusDimensionLevel: this.#dimensionLevelService.getDimensionLevel(dimensionItem, perspective),
      filterDimensions: ArrayTools.removeFalsy(Array.from(updatedFilters, ([, value]) => value)),
    });

    this.#monitoringService.logEvent('TreeFilterSelected', {
      dimensionItem: dimensionItem.code,
      dimensionItemLevel: dimensionItem.dimensionLevel,
      view: 'Tilegrid',
    });
  }
}
